// src/services/claude.js
// ─────────────────────────────────────────────────────────────────────
// ConcrePro — Claude Vision Service
//
// ARQUITECTURA DE PRIVACIDAD:
//   • La imagen se pasa como base64 al Worker de Cloudflare
//   • El Worker llama a Claude API y devuelve solo el JSON de diagnóstico
//   • La imagen NUNCA se almacena: el Worker es completamente stateless
//   • Después del diagnóstico, la referencia base64 se descarta en cliente
// ─────────────────────────────────────────────────────────────────────

import { SYSTEM_PROMPT }               from '../prompts/system.js';
import { ROUTER_PROMPT }               from '../prompts/router.js';
import { PROMPT_P01 }                  from '../prompts/p01_cracks.js';
import { PROMPT_P02 }                  from '../prompts/p02_spalling.js';
import { PROMPT_P03_STEP1, PROMPT_P03_STEP2 } from '../prompts/p03_delamination.js';
import { PROMPT_P04 }                  from '../prompts/p04_efflorescence.js';
import { PROMPT_P05 }                  from '../prompts/p05_popout_asr.js';
import { PROMPT_P06 }                  from '../prompts/p06_joints.js';
import { PROMPT_P07 }                  from '../prompts/p07_corrosion.js';
import { PROMPT_P08 }                  from '../prompts/p08_plastic_shrinkage.js';
import { PROMPT_P09_STEP1, PROMPT_P09_STEP2 } from '../prompts/p09_carbonation.js';
import { ACTIVE_SPONSOR }              from '../config/sponsor.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

// ─── PROMPT RESOLVER ────────────────────────────────────────────────
/**
 * Devuelve el prompt correcto para cada patología.
 * Para p03/p09 usa step1 si no hay input adicional, step2 si lo hay.
 *
 * @param {string} pathologyId - e.g. 'p01_cracks' | 'auto'
 * @param {string} [additionalInput] - resultado de percusión / pH
 * @returns {string} - prompt listo para enviar a Claude
 */
export function getPathologyPrompt(pathologyId, additionalInput = '') {
  const sponsor = ACTIVE_SPONSOR?.products?.[pathologyId] || '';

  switch (pathologyId) {
    case 'p01_cracks':         return PROMPT_P01(sponsor);
    case 'p02_spalling':       return PROMPT_P02(sponsor);
    case 'p03_delamination':
      return additionalInput
        ? PROMPT_P03_STEP2(additionalInput, sponsor)
        : PROMPT_P03_STEP1();
    case 'p04_efflorescence':  return PROMPT_P04(sponsor);
    case 'p05_popout_asr':     return PROMPT_P05(sponsor);
    case 'p06_joints':         return PROMPT_P06(sponsor);
    case 'p07_corrosion':      return PROMPT_P07(sponsor);
    case 'p08_plastic_shrinkage': return PROMPT_P08(sponsor);
    case 'p09_carbonation':
      return additionalInput
        ? PROMPT_P09_STEP2(additionalInput, sponsor)
        : PROMPT_P09_STEP1();
    default:
      return ROUTER_PROMPT;
  }
}

// ─── WORKER CALL ────────────────────────────────────────────────────
/**
 * Llama al Cloudflare Worker con imagen + prompt.
 * El Worker llama a la Claude API y devuelve el JSON de diagnóstico.
 *
 * @param {string} imageBase64 - JPEG comprimido en base64 (sin header "data:")
 * @param {string} userPrompt  - prompt construido por getPathologyPrompt()
 * @returns {Promise<object>}  - DiagnosisResult JSON parseado
 */
async function callWorker(imageBase64, userPrompt) {
  const response = await fetch(`${API_BASE}/diagnose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      imageBase64,
      systemPrompt: SYSTEM_PROMPT,
      userPrompt,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(`Worker error ${response.status}: ${err.message || 'Unknown error'}`);
  }

  const data = await response.json();

  // El Worker devuelve { diagnosis: string } — Claude responde con JSON string
  const raw = data.diagnosis;
  if (!raw) throw new Error('Respuesta vacía del Worker');

  // Limpiar posibles backticks de markdown
  const clean = raw.replace(/```json\n?|\n?```/g, '').trim();

  try {
    return JSON.parse(clean);
  } catch {
    throw new Error('No se pudo parsear la respuesta de Claude como JSON');
  }
}

// ─── PUBLIC API ─────────────────────────────────────────────────────

/**
 * routePathology
 * Primera llamada: identifica qué patología es visible en la imagen.
 * Debe llamarse ANTES de analyzeConcrete si pathologyId = 'auto'.
 *
 * @param {string} imageBase64
 * @returns {Promise<{ pathology_id: string, confidence: number, requires_additional_input: boolean, note: object }>}
 */
export async function routePathology(imageBase64) {
  return callWorker(imageBase64, ROUTER_PROMPT);
}

/**
 * analyzeConcrete
 * Diagnóstico completo para una patología específica.
 * Si pathologyId = 'auto', primero llama a routePathology() internamente.
 *
 * @param {string} imageBase64     - JPEG base64 sin header (máx 1024px, quality 0.85)
 * @param {string} pathologyId     - ID específico o 'auto'
 * @param {string} additionalInput - Resultado de prueba de campo (para p03/p09)
 * @returns {Promise<DiagnosisResult>}
 */
export async function analyzeConcrete(imageBase64, pathologyId = 'auto', additionalInput = '') {
  if (!imageBase64) throw new Error('imageBase64 es requerido');

  // Si es 'auto', identificar primero la patología
  let resolvedId = pathologyId;
  if (pathologyId === 'auto') {
    const routing = await routePathology(imageBase64);
    resolvedId = routing.pathology_id || 'unidentifiable';
  }

  // Obtener prompt específico para la patología
  const prompt = getPathologyPrompt(resolvedId, additionalInput);

  // Llamar al Worker
  const result = await callWorker(imageBase64, prompt);

  // Enriquecer con metadatos de sesión
  return {
    ...result,
    _meta: {
      pathology_id_resolved: resolvedId,
      timestamp: new Date().toISOString(),
      has_additional_input: Boolean(additionalInput),
    },
  };
}

/**
 * generateLinkedInDraft
 * Genera solo el borrador de LinkedIn para un diagnóstico ya realizado.
 * Útil si el draft incluido en el diagnóstico necesita regenerarse.
 *
 * @param {object} diagnosis - DiagnosisResult completo
 * @param {string} lang      - 'es' | 'en'
 * @returns {Promise<string>} - texto del post
 */
export async function generateLinkedInDraft(diagnosis, lang = 'es') {
  const linkedinPrompt = `
Based on this concrete pathology diagnosis, write a LinkedIn post as Arq. Eugenio Carrasco.
Diagnosis: ${JSON.stringify(diagnosis, null, 2)}

Rules:
- First person, signed as Arq. Eugenio Carrasco
- Explicitly mention ConcrePro as the diagnostic tool used
- Technical-divulgative tone
- Max 1300 characters
- End with a question to readers
- Include: #ConcrePro #PatologíaDelConcreto #ICRI #ACI #Sika + 2-3 relevant hashtags
- Language: ${lang === 'es' ? 'Spanish' : 'English'}

Return ONLY the post text, no JSON wrapper.`;

  const response = await fetch(`${API_BASE}/diagnose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      imageBase64: null,
      systemPrompt: 'You are a LinkedIn content writer for concrete engineering professionals. Return ONLY the post text.',
      userPrompt: linkedinPrompt,
      textOnly: true,
    }),
  });

  const data = await response.json();
  return data.diagnosis || data.text || '';
}
